import { useCallback, useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import api from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, DataTable, Loading, cx } from "./StudioShared";
import { Activity, Coins, Zap, Timer, CheckCircle2, ShieldAlert, UserCheck, Lightbulb } from "lucide-react";

const money = (c) => `$${Number(c || 0).toFixed(4)}`;
const money2 = (c) => `$${Number(c || 0).toFixed(2)}`;
const ms = (v) => (v ? `${(v / 1000).toFixed(1)}s` : "—");

const TABS = [
  ["overview", "Executive"], ["departments", "Departments"], ["missions", "Missions"],
  ["strategists", "Strategists"], ["workflows", "Workflows"], ["models", "Models"],
  ["explorer", "Cost Explorer"], ["suggestions", "Optimize"],
];

export default function CostDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <StudioPage title="AI Transparency & Cost Dashboard" testid="cost-dashboard-page"
      question="Every AI execution, token and dollar — by department, mission, strategist, workflow and model.">
      <Panel className="p-1.5 mb-6 flex flex-wrap gap-1" data-testid="cost-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cost-tab-${k}`}
            className={cx("px-3.5 py-2 text-sm font-medium rounded-sm transition-colors", tab === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white hover:bg-white/5")}>{l}</button>
        ))}
      </Panel>
      {tab === "overview" && <Overview />}
      {tab === "departments" && <Departments />}
      {tab === "missions" && <Missions />}
      {tab === "strategists" && <Strategists />}
      {tab === "workflows" && <Workflows />}
      {tab === "models" && <Models />}
      {tab === "explorer" && <Explorer />}
      {tab === "suggestions" && <Suggestions />}
    </StudioPage>
  );
}

const useEndpoint = (path) => {
  const [data, setData] = useState(null);
  useEffect(() => { let ok = true; api.get(path).then((r) => ok && setData(r.data)).catch(() => ok && setData(false)); return () => { ok = false; }; }, [path]);
  return data;
};

function Overview() {
  const d = useEndpoint("/admin/analytics/overview");
  const depts = useEndpoint("/admin/analytics/departments");
  if (d === null) return <Loading label="Loading telemetry…" />;
  if (!d) return <Empty />;
  const modes = d.by_mode || {};
  const chart = (depts || []).filter((x) => x.ai_cost > 0).slice(0, 8).map((x) => ({ name: x.name, cost: x.ai_cost }));
  return (
    <div className="space-y-6" data-testid="cost-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Total Executions" value={d.total_executions} icon={Activity} testid="ov-executions" />
        <Metric label="Total Tokens" value={d.total_tokens.toLocaleString()} icon={Zap} testid="ov-tokens" />
        <Metric label="Total Cost" value={money(d.total_cost)} icon={Coins} testid="ov-cost" />
        <Metric label="Avg Response" value={ms(d.avg_response_ms)} icon={Timer} testid="ov-latency" />
        <Metric label="Success Rate" value={`${d.success_rate}%`} icon={CheckCircle2} status={d.success_rate >= 95 ? "healthy" : "warning"} testid="ov-success" />
        <Metric label="Fallback Rate" value={`${d.fallback_rate}%`} icon={ShieldAlert} status={d.fallback_rate > 10 ? "warning" : "healthy"} testid="ov-fallback" />
        <Metric label="Human Approval Rate" value={`${d.human_approval_rate}%`} icon={UserCheck} testid="ov-approval" />
        <Metric label="Live AI Rate" value={`${d.live_rate}%`} sub={`${d.approvals_given} approved · ${d.approvals_rejected} rejected`} testid="ov-live" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="p-5 lg:col-span-2">
          <SectionLabel className="mb-4">AI Cost by Department</SectionLabel>
          {chart.length === 0 ? <p className="text-slate-500 text-sm">No department cost yet — run workflows tagged to a department.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chart} margin={{ left: -10 }}>
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0A1128", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4 }} formatter={(v) => money(v)} />
                <Bar dataKey="cost" radius={[4, 4, 0, 0]}>{chart.map((_, i) => <Cell key={i} fill="#D4AF37" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel className="p-5">
          <SectionLabel className="mb-4">Provenance (Transparency)</SectionLabel>
          <div className="space-y-3" data-testid="ov-modes">
            <ModeRow label="🟢 Live AI" value={modes.live || 0} total={d.total_executions} tone="bg-emerald-400" />
            <ModeRow label="🟡 Fallback" value={modes.fallback || 0} total={d.total_executions} tone="bg-amber-400" />
            <ModeRow label="⚪ Manual" value={modes.manual || 0} total={d.total_executions} tone="bg-slate-400" />
          </div>
          <SectionLabel className="mt-6 mb-3">By Module</SectionLabel>
          <div className="space-y-1.5">
            {Object.entries(d.by_module || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm"><span className="text-slate-300">{k}</span><span className="font-mono text-gold">{v}</span></div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

const ModeRow = ({ label, value, total, tone }) => (
  <div>
    <div className="flex justify-between text-sm mb-1"><span className="text-slate-300">{label}</span><span className="text-slate-400">{value}</span></div>
    <div className="h-1.5 bg-white/10 rounded-sm overflow-hidden"><div className={cx("h-full", tone)} style={{ width: `${total ? (value / total) * 100 : 0}%` }} /></div>
  </div>
);

function Departments() {
  const rows = useEndpoint("/admin/analytics/departments");
  if (rows === null) return <Loading />;
  const columns = [
    { key: "name", label: "Department", render: (r) => <span className="text-white flex items-center gap-2">{r.name}{r.is_core && <Badge tone="gold">core</Badge>}</span> },
    { key: "ai_cost", label: "AI Cost", render: (r) => <span className="font-mono text-gold">{money(r.ai_cost)}</span> },
    { key: "tokens", label: "Tokens", render: (r) => r.tokens.toLocaleString() },
    { key: "workflow_executions", label: "Runs", render: (r) => r.workflow_executions },
    { key: "avg_runtime_ms", label: "Avg Runtime", render: (r) => ms(r.avg_runtime_ms) },
    { key: "active_missions", label: "Missions", render: (r) => r.active_missions },
    { key: "active_strategists", label: "Strategists", render: (r) => r.active_strategists },
  ];
  return <DataTable testid="dept-table" columns={columns} rows={rows || []} empty="No department analytics yet." />;
}

function Missions() {
  const d = useEndpoint("/admin/analytics/missions");
  if (d === null) return <Loading />;
  const columns = [
    { key: "name", label: "Mission", render: (r) => <span className="text-white">{r.name}</span> },
    { key: "executions", label: "Runs" },
    { key: "cost", label: "Cost", render: (r) => <span className="font-mono text-gold">{money(r.cost)}</span> },
    { key: "avg_response_ms", label: "Avg Time", render: (r) => ms(r.avg_response_ms) },
    { key: "success_rate", label: "Success", render: (r) => `${r.success_rate}%` },
    { key: "failure_rate", label: "Failure", render: (r) => `${r.failure_rate}%` },
  ];
  return (
    <div className="space-y-6" data-testid="mission-analytics">
      <DataTable testid="mission-analytics-table" columns={columns} rows={d.items || []} empty="No mission executions yet — add Mission nodes to workflows and run them." />
      {(d.items || []).length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          <TopList title="Most Executed" items={d.most_executed} value={(x) => `${x.executions} runs`} />
          <TopList title="Highest Cost" items={d.highest_cost} value={(x) => money(x.cost)} />
        </div>
      )}
    </div>
  );
}

const TopList = ({ title, items, value }) => (
  <Panel className="p-5"><SectionLabel className="mb-3">{title}</SectionLabel>
    <div className="space-y-2">{(items || []).map((x, i) => (
      <div key={i} className="flex justify-between text-sm"><span className="text-slate-300 truncate">{x.name}</span><span className="font-mono text-gold shrink-0 ml-2">{value(x)}</span></div>
    ))}</div>
  </Panel>
);

function Strategists() {
  const rows = useEndpoint("/admin/analytics/strategists");
  if (rows === null) return <Loading />;
  const columns = [
    { key: "name", label: "Strategist", render: (r) => <span className="text-white">{r.name}</span> },
    { key: "department", label: "Department" },
    { key: "executions", label: "Runs" },
    { key: "cost", label: "Cost", render: (r) => <span className="font-mono text-gold">{money(r.cost)}</span> },
    { key: "avg_response_ms", label: "Avg Time", render: (r) => ms(r.avg_response_ms) },
    { key: "success_rate", label: "Success", render: (r) => `${r.success_rate}%` },
    { key: "assigned_missions", label: "Missions", render: (r) => (r.assigned_missions || []).length || "—" },
  ];
  return <DataTable testid="strat-analytics-table" columns={columns} rows={rows || []} empty="No strategist executions yet." />;
}

function Workflows() {
  const rows = useEndpoint("/admin/analytics/workflows");
  if (rows === null) return <Loading />;
  const columns = [
    { key: "name", label: "Workflow", render: (r) => <span className="text-white">{r.name}</span> },
    { key: "runs", label: "Runs" },
    { key: "completed", label: "Completed", render: (r) => <span className="text-emerald-400">{r.completed}</span> },
    { key: "failed", label: "Failed", render: (r) => <span className="text-red-400">{r.failed}</span> },
    { key: "waiting", label: "Waiting", render: (r) => <span className="text-amber-400">{r.waiting}</span> },
    { key: "avg_cost", label: "Avg Cost", render: (r) => <span className="font-mono text-gold">{money(r.avg_cost)}</span> },
    { key: "avg_tokens", label: "Avg Tokens", render: (r) => r.avg_tokens.toLocaleString() },
    { key: "avg_duration_ms", label: "Avg Duration", render: (r) => ms(r.avg_duration_ms) },
  ];
  return <DataTable testid="wf-analytics-table" columns={columns} rows={rows || []} empty="No workflow runs yet." />;
}

function Models() {
  const rows = useEndpoint("/admin/analytics/models");
  if (rows === null) return <Loading />;
  const chart = (rows || []).map((r) => ({ name: r.model, cost: r.cost }));
  const columns = [
    { key: "model", label: "Model", render: (r) => <span className="text-white">{r.model}</span> },
    { key: "provider", label: "Provider" },
    { key: "executions", label: "Runs" },
    { key: "tokens", label: "Tokens", render: (r) => r.tokens.toLocaleString() },
    { key: "cost", label: "Cost", render: (r) => <span className="font-mono text-gold">{money(r.cost)}</span> },
    { key: "avg_response_ms", label: "Avg Time", render: (r) => ms(r.avg_response_ms) },
    { key: "success_rate", label: "Success", render: (r) => `${r.success_rate}%` },
    { key: "failure_rate", label: "Failure", render: (r) => `${r.failure_rate}%` },
  ];
  return (
    <div className="space-y-6" data-testid="model-analytics">
      {chart.length > 0 && (
        <Panel className="p-5"><SectionLabel className="mb-4">Cost by Model</SectionLabel>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart} margin={{ left: -10 }}>
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0A1128", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4 }} formatter={(v) => money(v)} />
              <Bar dataKey="cost" radius={[4, 4, 0, 0]}>{chart.map((_, i) => <Cell key={i} fill="#D4AF37" />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}
      <DataTable testid="model-analytics-table" columns={columns} rows={rows || []} empty="No model usage yet." />
    </div>
  );
}

function Explorer() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ department: "", workflow: "", strategist: "", mission: "", model: "", provider: "", start: "", end: "" });
  const load = useCallback(async () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    const r = await api.get(`/admin/analytics/explorer?${p}`); setData(r.data);
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const facets = data?.facets || {};
  const sel = (key, label) => (
    <select value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`explorer-${key}`}
      className="bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none">
      <option value="">{label}</option>{(facets[key + "s"] || []).map((x) => <option key={x} value={x}>{x}</option>)}
    </select>
  );
  const columns = [
    { key: "date", label: "Date", render: (r) => (r.date ? new Date(r.date).toLocaleString() : "—") },
    { key: "workflow", label: "Workflow" },
    { key: "department", label: "Dept", render: (r) => r.department || "—" },
    { key: "label", label: "Node", render: (r) => <span className="text-white">{r.label}</span> },
    { key: "strategist", label: "Strategist", render: (r) => r.strategist || r.mission || "—" },
    { key: "model", label: "Model", render: (r) => r.model || "—" },
    { key: "tokens", label: "Tokens" },
    { key: "cost", label: "Cost", render: (r) => <span className="font-mono text-gold">{money(r.cost)}</span> },
  ];
  return (
    <div className="space-y-5" data-testid="cost-explorer">
      <Panel className="p-3 flex flex-wrap gap-2 items-center">
        {sel("department", "All departments")}
        {sel("workflow", "All workflows")}
        {sel("strategist", "All strategists")}
        {sel("model", "All models")}
        {sel("provider", "All providers")}
        <input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="explorer-start" className="bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none" />
        <button onClick={() => setF({ department: "", workflow: "", strategist: "", mission: "", model: "", provider: "", start: "", end: "" })} data-testid="explorer-reset" className="text-slate-400 hover:text-white text-sm px-3 py-2">Reset</button>
      </Panel>
      {data && (
        <div className="flex gap-4">
          <Metric label="Events" value={data.totals.events} className="flex-1" testid="explorer-events" />
          <Metric label="Cost" value={money(data.totals.cost)} className="flex-1" testid="explorer-cost" />
          <Metric label="Tokens" value={data.totals.tokens.toLocaleString()} className="flex-1" testid="explorer-tokens" />
        </div>
      )}
      <DataTable testid="explorer-table" columns={columns} rows={data?.events || []} empty="No matching events. Run workflows to build the cost ledger." />
    </div>
  );
}

function Suggestions() {
  const d = useEndpoint("/admin/analytics/suggestions");
  if (d === null) return <Loading />;
  return (
    <div className="space-y-3" data-testid="cost-suggestions">
      {(d.suggestions || []).map((s, i) => (
        <Panel key={i} className="p-5 flex items-start gap-4" data-testid={`suggestion-${i}`}>
          <div className="w-10 h-10 rounded-sm bg-gold/10 border border-gold/40 flex items-center justify-center shrink-0"><Lightbulb size={18} className="text-gold" /></div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1"><span className="text-white font-medium">{s.target}</span><Badge tone={s.type === "cost" ? "gold" : s.type === "reliability" ? "amber" : "slate"}>{s.type}</Badge>{s.avg_cost != null && <span className="text-xs text-slate-500">avg {money(s.avg_cost)}/run</span>}</div>
            <p className="text-slate-300 text-sm">{s.suggestion}</p>
          </div>
          {s.estimated_savings_pct != null && (
            <div className="text-right shrink-0"><div className="text-2xl font-light text-emerald-400 font-mono">~{s.estimated_savings_pct}%</div><div className="text-[10px] uppercase tracking-wider text-slate-500">est. savings</div></div>
          )}
        </Panel>
      ))}
    </div>
  );
}

const Empty = () => <div className="text-slate-500 text-sm py-10 text-center">No telemetry yet — run some AI generations and workflows.</div>;
